import { PortActionTypes } from '@shared/utils/constants';
import PortCall from '@api/models/port-call';
import PortCallAction from '@api/models/port-call-action';
import RobBunker from '@api/models/rob-bunker';
import Voyage from '@api/models/voyage';
import Bunker from '@api/models/bunker';
import VesselBunker from '@api/models/vessel-bunker';
import {
	ItineraryPortCallDto,
	ItineraryPortCallDtoActions,
} from '@client/screens/estimates/details/helpers/types';
import APIError from '../../../api/src/utils/APIError';

type PortCallAndActions = PortCall & {
	PortCallActions: Array<PortCallAction & {
		RobBunkers?: Array<RobBunker>;
		VesselBunkers?: Array<VesselBunker & { Bunker: Bunker }>;
	}>;
};

/*
* Helper functions for getting actions from port calls and checking
* for certain attributes. Use the "model" version on backend, and the "dto" version on frontend
*/

// PortCall versions

export const isPortCallCommencementModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.COMMENCEMENT);
};

export const isPortCallCompletionModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.COMPLETION);
};

export const isPortCallDeliveryModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.DELIVERY);
};

export const isPortCallRedeliveryModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.REDELIVERY);
};

export const isPortCallTcInRedeliveryModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.REDELIVERING_TC_IN);
};

export const isPortCallBunkerStemModel = (
	portCall: PortCallAndActions,
) => {
	return portCall?.PortCallActions
		?.some((pca) => pca.event === PortActionTypes.BUNKER_STEM);
};

// DTO versions

export const isPortCallCommencementDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.COMMENCEMENT);
};

export const isPortCallCompletionDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.COMPLETION);
};

export const isPortCallDeliveryDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.DELIVERY);
};

export const isPortCallRedeliveryDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.REDELIVERY);
};

export const isPortCallTcInRedeliveryDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.REDELIVERING_TC_IN);
};

export const isPortCallBunkerStemDto = (
	portCall: ItineraryPortCallDto,
) => {
	return portCall?.actions
		?.some((pca) => pca.event === PortActionTypes.BUNKER_STEM);
};

export const isPortCallBunkerStem = (
	portCall: PortCallAndActions | ItineraryPortCallDto,
) => {
	if ('actions' in portCall) {
		return isPortCallBunkerStemDto(portCall);
	}

	return isPortCallBunkerStemModel(portCall);
};

export const isPortActionSpecial = (
	portCallAction: PortCallAction,
) => {
	const { event } = portCallAction;

	return event === (
		PortActionTypes.DELIVERY ||
		PortActionTypes.REDELIVERY ||
		PortActionTypes.REDELIVERING_TC_IN
	);
};

// "Get" action functions split into model and DTO versions

export const getDeliveryActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.DELIVERY);
};

export const getDeliveryActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall?.actions
		?.find((pca) => pca.event === PortActionTypes.DELIVERY);
};

export const getRedeliveryActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.REDELIVERY);
};

export const getRedeliveryActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall?.actions
		?.find((pca) => pca.event === PortActionTypes.REDELIVERY);
};

export const getTcInRedeliveryActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.REDELIVERING_TC_IN);
};

export const getTcInRedeliveryActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall?.actions
		?.find((pca) => pca.event === PortActionTypes.REDELIVERING_TC_IN);
};

export const getCompletionActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.COMPLETION);
};

export const getCompletionActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall.actions
		.find((pca) => pca.event === PortActionTypes.COMPLETION);
};

export const getArrivalActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.ARRIVAL);
};

export const getArrivalActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall?.actions
		?.find((pca) => pca.event === PortActionTypes.ARRIVAL);
};

export const getDepartureActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.DEPARTURE);
};

export const getDepartureActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall.actions
		.find((pca) => pca.event === PortActionTypes.DEPARTURE);
};

export const getCommencementActionModel = (
	portCall: PortCallAndActions,
): PortCallAndActions['PortCallActions'][number] | undefined => {
	return portCall?.PortCallActions
		?.find((pca) => pca.event === PortActionTypes.COMMENCEMENT);
};

export const getCommencementActionDto = (
	portCall: ItineraryPortCallDto,
): ItineraryPortCallDtoActions[number] | undefined => {
	return portCall?.actions
		?.find((pca) => pca.event === PortActionTypes.COMMENCEMENT);
};

export const findTcInRedeliveryAction = (
	voyage: Voyage & {
		PortCalls: Array<PortCall & { PortCallActions: Array<PortCallAction> }>;
	},
): PortCallAndActions['PortCallActions'][number] | undefined => {
	const relevantPortCalls = voyage.PortCalls.filter((pc) => getTcInRedeliveryActionModel(pc) != null);

	if (relevantPortCalls.length > 1) {
		throw new APIError(400, `More than one TC IN redelivery event found on contract ${voyage.id}`);
	}

	return getTcInRedeliveryActionModel(relevantPortCalls[0]);
};
